import React, { useEffect, useState, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { Box, Flex, Stack, Text } from '../../../vanilla'
import { LockIcon } from '../../../components/icons'
import LoadingOverlay from '../../../components/loading-overlay'
import { useGlobalStore, useOrderStore } from '../../../store/hooks/useStore'
import { FetchPaymentMethodsPayload } from 'braintree-web'
import { useCheckoutState } from '../../../contexts'
import { CheckoutBonusCardStatus, FilteredPaymentMethod } from '../../../types/store/common'
import { UpdatePaymentAmount } from './update-payment-amount'
import { PaymentOptions } from './payment-options'
import { BraintreeInputsProps } from '../../../types/braintree'
import { CheckoutErrorMessage } from './checkout-error-message'
import { PAYMENT_METHOD_IDS } from '../../../utils/constants'

interface PaymentSectionProps {
  submitOrderProcess: () => Promise<void | null>
  userVaultedCardsLoaded: boolean
  bonusCardStatus: CheckoutBonusCardStatus
  userVaultedCards: FetchPaymentMethodsPayload[]
  selectedPayment: string | null
  setSelectedPayment: React.Dispatch<React.SetStateAction<string | null>>
  usingSavedCard?: boolean
  braintreeInputs: BraintreeInputsProps
  hostedFieldsValidationErrors: {
    cardholderName: boolean
    number: boolean
    expirationDate: boolean
    cvv: boolean
  }
  braintreeFormIsValid: boolean
  hostedFieldsErrors: boolean
  isTopUpPage: boolean
  paymentMethods: FilteredPaymentMethod[]
}

export const PaymentSection = observer((props: PaymentSectionProps) => {
  const {
    submitOrderProcess,
    userVaultedCardsLoaded,
    bonusCardStatus,
    userVaultedCards,
    selectedPayment,
    setSelectedPayment,
    usingSavedCard = false,
    braintreeInputs,
    hostedFieldsValidationErrors,
    braintreeFormIsValid,
    hostedFieldsErrors,
    isTopUpPage,
    paymentMethods
  } = props

  const {
    customSitePreferences,
    isCustomSitePreferenceSet,
    getCustomSitePreferenceById,
  } = useGlobalStore()

  const { isPaymentRefunding, isPaymentRequired, clearpaySetupData } = useOrderStore()

  const { setSaveCreditCard } = useCheckoutState()
  const [savedCardEnabled, setSavedCardEnabled] = useState<boolean>(false)
  const [allowSaveCreditCard, setAllowSaveCreditCard] = useState<boolean>(false)
  const clearpayEnabled = paymentMethods.find((method) => method.id === PAYMENT_METHOD_IDS.CLEARPAY) !== undefined
  const clearpayAvailable = clearpaySetupData.button_rendered

  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({
    cardNumber: null,
    cardName: null,
    cardExpiry: null,
    cardCvv: null,
  })

  useEffect(() => {
    const handleAutoFill = (event: AnimationEvent) => {
      const target = event.target as HTMLInputElement
      target.dispatchEvent(new Event('input', { bubbles: true }))
    }

    const inputs = Object.values(inputRefs.current)
    inputs.forEach((input) => {
      input?.addEventListener('animationstart', handleAutoFill)
    })

    return () => {
      inputs.forEach((input) => {
        input?.removeEventListener('animationstart', handleAutoFill)
      })
    }
  }, [])

  // Checks custom pref for allowing saved cards
  useEffect(() => {
     (async function () {
      if (!isCustomSitePreferenceSet('BRAINTREE_savedCardEnabled')) {
        await getCustomSitePreferenceById('BRAINTREE_savedCardEnabled')
      }
      setSavedCardEnabled(customSitePreferences['BRAINTREE_savedCardEnabled'] as boolean)
    })()
  })

  // Checks custom pref for Braintree Vault Mode
  useEffect(() => {
     (async function () {
      if (!isCustomSitePreferenceSet('BRAINTREE_Vault_Mode')) {
        await getCustomSitePreferenceById('BRAINTREE_Vault_Mode')
      }
      setAllowSaveCreditCard(customSitePreferences['BRAINTREE_Vault_Mode'] as boolean)
    })()
  })

  return (
    <Stack
      borderRadius={[null, null, 'base']}
      position="relative"
      bg="white"
      boxShadow="base"
      gap="0px"
    >
      <Box borderBottom="1px" borderColor="border1" padding={['16px', '16px', '16px']} data-test-selector={isTopUpPage ? 'top-up-method-section' : 'payment-method-section'}>
        <Flex flexDirection={['column', 'row']} justify="space-between" alignItems={['flex-start', 'center']}>
          <Stack alignItems="baseline" direction="row">
            <LockIcon />
            <Text variant="heading1">{isTopUpPage ? 'Top up' : 'Payment'} Method</Text>
          </Stack>
        </Flex>
      </Box>
      <LoadingOverlay
        isLoading={
          !bonusCardStatus?.hidePayment &&
          !isPaymentRefunding &&
          isPaymentRequired && 
          (clearpayEnabled && !clearpayAvailable) || (!userVaultedCardsLoaded)
        }
      />
      <CheckoutErrorMessage isTopUpPage={isTopUpPage} />
      {(!isPaymentRequired || bonusCardStatus?.hidePayment) && !isTopUpPage ?
        <UpdatePaymentAmount submitOrderProcess={submitOrderProcess} />
        : <PaymentOptions
          allowSaveCreditCard={allowSaveCreditCard}
          bonusCardStatus={bonusCardStatus}
          braintreeFormIsValid={braintreeFormIsValid}
          braintreeInputs={braintreeInputs}
          hostedFieldsErrors={hostedFieldsErrors}
          hostedFieldsValidationErrors={hostedFieldsValidationErrors}
          isTopUp={isTopUpPage}
          inputRefs={inputRefs}
          savedCardEnabled={savedCardEnabled}
          selectedPayment={selectedPayment}
          setSaveCreditCard={setSaveCreditCard}
          submitOrderProcess={submitOrderProcess}
          usingSavedCard={usingSavedCard}
          setSelectedPayment={setSelectedPayment}
          userVaultedCards={userVaultedCards}
          paymentMethods={paymentMethods}
        />
      }
    </Stack>
  )
})
